import * as React from 'react'
import { MailWarning, Plus, ShieldCheck } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Notice } from '@/components/ui/notice'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { PageHeader } from '@/components/visitone/page-header'
import { ServerDataTable, type TableRow, type TableState } from '@/components/visitone/server-data-table'
import { RefusedSurface } from '@/components/visitone/surfaces/access-surface'
import { useT } from '@/components/visitone/visitone-provider'
import { useAccess } from '@/hooks/use-access'
import { useLandlordMutations, useLandlords } from '@/hooks/use-landlords'
import { LandlordRequestError, type Landlord } from '@/lib/bff/landlords'
import { pathForDestination } from '@/lib/routing'

/** Router state that opens the Add a Landlord dialog, e.g. from the command palette. */
export type LandlordsRouteState = { openAddLandlord?: boolean }

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const displayName = (landlord: Landlord) => landlord.name ?? landlord.email ?? landlord.id
const emailOf = (landlord: Landlord) => landlord.email ?? landlord.name ?? landlord.id
const errorCode = (error: unknown) => (error instanceof LandlordRequestError ? error.code : null)

/* The latest action result. Only one is shown; each new result replaces the last. */
type Result =
  | { kind: 'added'; email: string }
  | { kind: 'addedEmailFailed'; email: string }
  | { kind: 'removed'; name: string }
  | { kind: 'resendSent'; email: string }
  | { kind: 'resendFailed'; email: string }
  | { kind: 'resendAlready'; email: string }

/*
 * Setup › Landlords (VIS-328): VisitOne staff who can act in every tenant.
 * The route renders this only for a caller whose access holds
 * `landlords.manage` (VIS-329); if the BFF still refuses the list, the page
 * is refused like any other. The BFF decides, per row, whether someone can be
 * removed or sent the set-password link again.
 */
export function LandlordsSurface() {
  const t = useT()
  const { canOpenSetupHome } = useAccess()
  const location = useLocation()
  const navigate = useNavigate()
  const mutations = useLandlordMutations()
  const rootRef = React.useRef<HTMLDivElement>(null)
  const emailRef = React.useRef<HTMLInputElement>(null)
  const addButtonId = React.useId()
  const closeButtonId = React.useId()

  const [page, setPage] = React.useState(0)
  const list = useLandlords(page, true)
  const landlords = React.useMemo(() => list.page?.data ?? [], [list.page])

  const [result, setResult] = React.useState<Result | null>(null)
  const [sendingId, setSendingId] = React.useState<string | null>(null)

  const [addOpen, setAddOpen] = React.useState(false)
  const [email, setEmail] = React.useState('')
  const [emailError, setEmailError] = React.useState<string>()
  const [addFailed, setAddFailed] = React.useState(false)
  const [adding, setAdding] = React.useState(false)

  const [removing, setRemoving] = React.useState<Landlord | null>(null)
  const [removeError, setRemoveError] = React.useState<'failed' | 'last' | null>(null)
  const [removeBusy, setRemoveBusy] = React.useState(false)

  const focusHeading = React.useCallback(() => {
    requestAnimationFrame(() => {
      const heading = rootRef.current?.querySelector<HTMLElement>('h1')
      if (!heading) return
      heading.setAttribute('tabindex', '-1')
      heading.focus()
    })
  }, [])

  const openAdd = React.useCallback(() => {
    setEmail('')
    setEmailError(undefined)
    setAddFailed(false)
    setAddOpen(true)
  }, [])

  // The palette's "Add Landlord" arrives with router state.
  const openFromRoute = (location.state as LandlordsRouteState | null)?.openAddLandlord === true
  React.useEffect(() => {
    if (!openFromRoute) return
    openAdd()
    navigate(location.pathname, { replace: true, state: null })
  }, [openFromRoute, openAdd, navigate, location.pathname])

  // A page emptied by removals elsewhere falls back to the last page that exists.
  const emptyLaterPage = page > 0 && list.page !== undefined && landlords.length === 0
  React.useEffect(() => {
    if (emptyLaterPage) setPage(Math.max(0, (list.page?.pagination.totalPages ?? 1) - 1))
  }, [emptyLaterPage, list.page])

  React.useEffect(() => {
    if (removeError === 'last') document.getElementById(closeButtonId)?.focus()
  }, [removeError, closeButtonId])

  const listForbidden = list.error instanceof LandlordRequestError && list.error.status === 403

  let state: TableState
  if (list.error) state = 'error'
  else if (!list.page || emptyLaterPage) state = 'loading'
  // The signed-in Landlord is always on the list, so an empty first page means the load went wrong.
  else if (landlords.length === 0) state = 'error'
  else state = 'data'

  const retry = () => void list.revalidate()

  const submitAdd = async (event: React.FormEvent) => {
    event.preventDefault()
    if (adding) return
    const value = email.trim()
    const invalid = (message: string) => {
      setEmailError(message)
      emailRef.current?.focus()
    }
    if (!value) return invalid(t('landlords.addDialog.emailRequired'))
    if (!EMAIL_FORMAT.test(value)) return invalid(t('landlords.addDialog.emailFormat'))
    if (landlords.some((landlord) => landlord.email?.toLowerCase() === value.toLowerCase())) {
      return invalid(t('landlords.addDialog.alreadyLandlord', { email: value }))
    }

    setAddFailed(false)
    setAdding(true)
    try {
      const added = await mutations.add(value)
      const addedEmail = added.landlord.email ?? value
      setResult(added.passwordEmailSent ? { kind: 'added', email: addedEmail } : { kind: 'addedEmailFailed', email: addedEmail })
      setAddOpen(false)
      void list.revalidate()
      requestAnimationFrame(() => document.getElementById(addButtonId)?.focus())
    } catch (error) {
      const code = errorCode(error)
      if (code === 'already_landlord') invalid(t('landlords.addDialog.alreadyLandlord', { email: value }))
      else if (code === 'venue_user') invalid(t('landlords.addDialog.venueUser', { email: value }))
      else if (code === 'invalid_email') invalid(t('landlords.addDialog.emailFormat'))
      else setAddFailed(true)
    } finally {
      setAdding(false)
    }
  }

  const openRemove = (landlord: Landlord) => {
    setRemoveError(null)
    setRemoving(landlord)
  }

  const confirmRemove = async () => {
    if (!removing || removeBusy) return
    setRemoveBusy(true)
    setRemoveError(null)
    try {
      await mutations.remove(removing.id)
      setResult({ kind: 'removed', name: displayName(removing) })
      setRemoving(null)
      void list.revalidate()
      focusHeading()
    } catch (error) {
      if (errorCode(error) === 'last_landlord') {
        setRemoveError('last')
        void list.revalidate()
      } else {
        setRemoveError('failed')
      }
    } finally {
      setRemoveBusy(false)
    }
  }

  const sendAgain = async (landlord: Landlord) => {
    if (sendingId) return
    const to = emailOf(landlord)
    setResult(null)
    setSendingId(landlord.id)
    try {
      await mutations.resendLink(landlord.id)
      setResult({ kind: 'resendSent', email: to })
    } catch (error) {
      if (errorCode(error) === 'already_signed_in') {
        setResult({ kind: 'resendAlready', email: to })
        void list.revalidate()
        focusHeading()
      } else {
        setResult({ kind: 'resendFailed', email: to })
      }
    } finally {
      setSendingId(null)
    }
  }

  const byId = new Map(landlords.map((landlord) => [landlord.id, landlord]))
  const rows: TableRow[] = landlords.map((landlord) => {
    const pills = [
      ...(landlord.isSelf ? [{ tone: 'neutral' as const, label: t('landlords.row.you') }] : []),
      ...(!landlord.signedIn ? [{ tone: 'warning' as const, label: t('landlords.row.notSignedIn') }] : []),
    ]
    return {
      id: landlord.id,
      label: displayName(landlord),
      cells: {
        landlord: {
          primary: displayName(landlord),
          secondary: landlord.name && landlord.email ? landlord.email : undefined,
          pills,
        },
      },
    }
  })

  const renderActions = (row: TableRow) => {
    const landlord = byId.get(row.id)
    if (!landlord) return null
    const sending = sendingId === landlord.id
    return (
      <>
        {landlord.canResendLink ? (
          <Button
            variant="secondary"
            size="sm"
            aria-label={t(sending ? 'landlords.row.sendingName' : 'landlords.row.sendAgainName', {
              email: emailOf(landlord),
            })}
            aria-disabled={sending || undefined}
            onClick={() => void sendAgain(landlord)}
          >
            {sending ? t('landlords.row.sending') : t('landlords.row.sendAgain')}
          </Button>
        ) : null}
        {landlord.removal === 'self' ? (
          <span className="text-xs text-fg-muted">{t('landlords.row.cannotRemoveSelf')}</span>
        ) : landlord.removal === 'onlyLandlord' ? (
          <span className="text-xs text-fg-muted">{t('landlords.row.onlyLandlord')}</span>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            aria-label={t('landlords.row.removeName', { name: displayName(landlord) })}
            onClick={() => openRemove(landlord)}
          >
            {t('landlords.row.remove')}
          </Button>
        )}
      </>
    )
  }

  const pagination = list.page?.pagination

  if (listForbidden) return <RefusedSurface />

  return (
    <div ref={rootRef} className="flex max-w-5xl flex-col gap-4">
      <PageHeader
        breadcrumb={[
          { label: t('setup.title'), to: canOpenSetupHome ? pathForDestination('setup') : undefined },
          t('landlords.title'),
        ]}
        title={t('landlords.title')}
        summary={t('landlords.summary')}
        action={
          <Button id={addButtonId} variant="primary" onClick={openAdd}>
            <Plus />
            {t('landlords.add')}
          </Button>
        }
      />

      <Notice tone="info" icon={<ShieldCheck className="size-5" />} title={t('landlords.notice.title')}>
        {t('landlords.notice.body')}
      </Notice>

      <ResultMessage result={result} />

      <ServerDataTable
        state={state}
        onRetry={retry}
        columns={[{ key: 'landlord', label: t('landlords.table.column') }]}
        rows={rows}
        sortedBy={{ key: 'landlord', direction: 'asc' }}
        rowActions={{ label: t('landlords.table.actions'), render: renderActions }}
        caption={t('landlords.table.caption')}
        regionLabel={t('landlords.table.caption')}
        footer={
          state === 'loading'
            ? t('landlords.table.loading')
            : t('landlords.table.count', { count: pagination?.totalCount ?? landlords.length })
        }
        pagination={pagination ? { page: pagination.page, totalPages: pagination.totalPages, onPageChange: setPage } : undefined}
        loadingRowCount={4}
        messages={{
          loadErrorTitle: t('landlords.loadFailed.title'),
          loadErrorBody: t('landlords.loadFailed.body'),
          retry: t('landlords.loadFailed.retry'),
        }}
      />

      <p role="status" className="sr-only">
        {sendingId ? t('landlords.row.sendingStatus') : ''}
      </p>

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        labelledBy="add-landlord-title"
        describedBy="add-landlord-description"
      >
        <form onSubmit={submitAdd} noValidate className="flex flex-col gap-4">
          <div>
            <h2 id="add-landlord-title" className="text-lg font-semibold text-fg-strong">
              {t('landlords.addDialog.title')}
            </h2>
            <p id="add-landlord-description" className="mt-1 text-sm text-fg-muted">
              {t('landlords.addDialog.body')}
            </p>
          </div>
          {addFailed ? (
            <OperationFeedback
              state="error"
              title={t('landlords.addDialog.failedTitle')}
              detail={t('landlords.addDialog.failedBody')}
            />
          ) : null}
          <Field
            id="landlord-email"
            label={t('landlords.addDialog.email')}
            help={t('landlords.addDialog.emailHelp')}
            required
            error={emailError}
          >
            <Input
              ref={emailRef}
              type="email"
              autoComplete="off"
              autoFocus
              aria-required="true"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
                setEmailError(undefined)
                setAddFailed(false)
              }}
            />
          </Field>
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={() => setAddOpen(false)}>
              {t('landlords.addDialog.cancel')}
            </Button>
            <Button variant="primary" type="submit" aria-disabled={adding || undefined}>
              {t('landlords.addDialog.submit')}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={removing !== null}
        onClose={() => setRemoving(null)}
        labelledBy="remove-landlord-title"
        describedBy="remove-landlord-description"
      >
        {removing ? (
          <div className="flex flex-col gap-4">
            <div>
              <h2 id="remove-landlord-title" className="text-lg font-semibold text-fg-strong wrap-anywhere">
                {t('landlords.removeDialog.title', { name: displayName(removing) })}
              </h2>
              <p id="remove-landlord-description" className="mt-1 text-sm text-fg-muted wrap-anywhere">
                {t('landlords.removeDialog.body', { email: emailOf(removing) })}
              </p>
            </div>
            {removeError === 'failed' ? (
              <OperationFeedback
                state="error"
                title={t('landlords.removeDialog.failedTitle')}
                detail={t('landlords.removeDialog.failedBody')}
              />
            ) : null}
            {removeError === 'last' ? (
              <OperationFeedback
                state="error"
                title={t('landlords.removeDialog.lastTitle')}
                detail={t('landlords.removeDialog.lastBody', { email: emailOf(removing) })}
              />
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              <Button id={closeButtonId} variant="secondary" autoFocus onClick={() => setRemoving(null)}>
                {removeError === 'last' ? t('landlords.removeDialog.close') : t('landlords.removeDialog.cancel')}
              </Button>
              {removeError === 'last' ? null : (
                <Button variant="destructive" aria-disabled={removeBusy || undefined} onClick={() => void confirmRemove()}>
                  {t('landlords.removeDialog.confirm')}
                </Button>
              )}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}

function ResultMessage({ result }: { result: Result | null }) {
  const t = useT()
  if (!result) return null
  switch (result.kind) {
    case 'added':
      return <OperationFeedback state="success" title={t('landlords.added', { email: result.email })} />
    case 'addedEmailFailed':
      return (
        <Notice
          tone="warning"
          live
          icon={<MailWarning className="size-5" />}
          title={t('landlords.addedEmailFailed.title', { email: result.email })}
        >
          {t('landlords.addedEmailFailed.body')}
        </Notice>
      )
    case 'removed':
      return <OperationFeedback state="success" title={t('landlords.removed', { name: result.name })} />
    case 'resendSent':
      return <OperationFeedback state="success" title={t('landlords.resend.sent', { email: result.email })} />
    case 'resendFailed':
      return (
        <OperationFeedback
          state="error"
          title={t('landlords.resend.failedTitle', { email: result.email })}
          detail={t('landlords.resend.failedBody')}
        />
      )
    case 'resendAlready':
      return (
        <Notice tone="info" live title={t('landlords.resend.alreadyTitle', { email: result.email })}>
          {t('landlords.resend.alreadyBody')}
        </Notice>
      )
  }
}
